import { alpha, createTheme } from '@mui/material';
import { UI_THEME } from '@bale-bot/config';

/**
 * Glass-oriented panel theme. Surfaces use translucent fills + blur so the
 * ambient page gradient shows through in both light and dark mode.
 */
export function createAppTheme(mode: 'light' | 'dark') {
  const isDark = mode === 'dark';

  const bgDefault = isDark ? '#0b0f1a' : '#e8eef8';
  const bgPaper = isDark ? alpha('#1a2236', 0.55) : alpha('#ffffff', 0.65);
  const border = isDark ? alpha('#ffffff', 0.1) : alpha('#1a2236', 0.08);
  const textPrimary = isDark ? '#f1f5f9' : '#0f172a';
  const textSecondary = isDark ? alpha('#e2e8f0', 0.72) : alpha('#334155', 0.78);

  const radius = Math.max(UI_THEME.borderRadius ?? 12, 14);

  return createTheme({
    palette: {
      mode,
      primary: { main: UI_THEME.primaryColor },
      secondary: { main: UI_THEME.secondaryColor },
      background: {
        default: bgDefault,
        paper: bgPaper,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
      },
      divider: border,
    },
    shape: { borderRadius: radius },
    typography: {
      fontFamily: UI_THEME.fontFamily,
      h4: { fontWeight: 650, letterSpacing: '-0.02em' },
      h5: { fontWeight: 650, letterSpacing: '-0.02em' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em' },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shadows: [
      'none',
      isDark
        ? `0 4px 24px ${alpha('#000', 0.35)}`
        : `0 4px 24px ${alpha('#1a2236', 0.08)}`,
      isDark
        ? `0 8px 32px ${alpha('#000', 0.4)}`
        : `0 8px 32px ${alpha('#1a2236', 0.1)}`,
      ...Array(22).fill(
        isDark
          ? `0 12px 40px ${alpha('#000', 0.45)}`
          : `0 12px 40px ${alpha('#1a2236', 0.12)}`,
      ),
    ] as unknown as ReturnType<typeof createTheme>['shadows'],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: bgDefault,
            backgroundAttachment: 'fixed',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: bgPaper,
            backdropFilter: 'blur(12px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? `0 8px 32px ${alpha('#000', 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
              : `0 8px 32px ${alpha('#1a2236', 0.06)}, inset 0 1px 0 ${alpha('#fff', 0.8)}`,
            transition:
              'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease, background-color 0.25s ease',
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: bgPaper,
            backdropFilter: 'blur(12px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? `0 8px 32px ${alpha('#000', 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
              : `0 8px 32px ${alpha('#1a2236', 0.06)}, inset 0 1px 0 ${alpha('#fff', 0.8)}`,
            transition:
              'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark
                ? `0 14px 40px ${alpha('#000', 0.45)}, inset 0 1px 0 ${alpha('#fff', 0.08)}`
                : `0 14px 40px ${alpha('#1a2236', 0.1)}, inset 0 1px 0 ${alpha('#fff', 0.9)}`,
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? alpha('#0b0f1a', 0.55)
              : alpha('#ffffff', 0.55),
            backdropFilter: 'blur(14px) saturate(1.25)',
            WebkitBackdropFilter: 'blur(14px) saturate(1.25)',
            borderBottom: `1px solid ${border}`,
            boxShadow: 'none',
            color: textPrimary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark
              ? alpha('#0f1524', 0.72)
              : alpha('#ffffff', 0.72),
            backdropFilter: 'blur(14px) saturate(1.25)',
            WebkitBackdropFilter: 'blur(14px) saturate(1.25)',
            borderRight: `1px solid ${border}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius - 2,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:active': { transform: 'scale(0.98)' },
          },
          contained: {
            boxShadow: isDark
              ? `0 4px 16px ${alpha(UI_THEME.primaryColor, 0.35)}`
              : `0 4px 16px ${alpha(UI_THEME.primaryColor, 0.25)}`,
          },
          outlined: {
            borderColor: border,
            backgroundColor: isDark
              ? alpha('#fff', 0.03)
              : alpha('#fff', 0.4),
            backdropFilter: 'blur(8px)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(8px)',
            border: `1px solid ${border}`,
          },
          outlined: {
            backgroundColor: isDark
              ? alpha('#fff', 0.04)
              : alpha('#fff', 0.5),
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius - 4,
            marginBottom: 4,
            transition: 'background-color 0.2s ease, transform 0.15s ease',
            '&.Mui-selected': {
              backgroundColor: isDark
                ? alpha(UI_THEME.primaryColor, 0.22)
                : alpha(UI_THEME.primaryColor, 0.12),
              '&:hover': {
                backgroundColor: isDark
                  ? alpha(UI_THEME.primaryColor, 0.3)
                  : alpha(UI_THEME.primaryColor, 0.18),
              },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark
                ? alpha('#000', 0.25)
                : alpha('#fff', 0.55),
              backdropFilter: 'blur(8px)',
              transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              '& fieldset': { borderColor: border },
              '&:hover fieldset': {
                borderColor: isDark
                  ? alpha('#fff', 0.2)
                  : alpha('#1a2236', 0.18),
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: isDark
              ? alpha('#151c2e', 0.88)
              : alpha('#ffffff', 0.88),
            backdropFilter: 'blur(16px) saturate(1.25)',
            WebkitBackdropFilter: 'blur(16px) saturate(1.25)',
            border: `1px solid ${border}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: border },
          head: {
            fontWeight: 600,
            color: textSecondary,
            backgroundColor: isDark
              ? alpha('#000', 0.2)
              : alpha('#fff', 0.35),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(12px)',
            border: `1px solid ${border}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backdropFilter: 'blur(10px)',
            backgroundColor: isDark
              ? alpha('#0f1524', 0.9)
              : alpha('#1e293b', 0.92),
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(12px)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.15s ease',
            '&.MuiTableRow-hover:hover': {
              backgroundColor: isDark
                ? alpha('#fff', 0.04)
                : alpha('#1a2236', 0.03),
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? alpha('#fff', 0.08)
              : alpha('#1a2236', 0.08),
          },
        },
      },
    },
  });
}
